import { useLayoutEffect, type CSSProperties, type ReactNode } from 'react';
import type { Sector } from '../variants/c/sectors';
import { alpha, lift, phoneData } from './data';
import { Brand, Hero, Spark } from './bits';
import { ComposedHome } from './owner';
import { COMPOSE, PASSPORT_NOTE, PASSPORT_TABS, passportIcon } from './compose';
import { Icon, type IconName } from './icons';
import { PHONE_CSS, SKIN_CSS } from './styles';
import { OWNER_CSS } from './ownerStyles';

// The owner experience phones: what opens when someone scans the maker's code.
// A dark, native-feeling screen system in the maker's accent. BrandedMark stays in the background.
// Every screen is drawn at one size (252 x 520) and scaled with `zoom`, so type and spacing stay exact.

export { phoneData } from './data';

function usePhoneCss() {
  useLayoutEffect(() => {
    if (document.getElementById('op-css')) return;
    const el = document.createElement('style');
    el.id = 'op-css';
    el.textContent = PHONE_CSS + SKIN_CSS + OWNER_CSS;
    document.head.appendChild(el);
  }, []);
}

const vars = (accent: string) =>
  ({
    '--op-a': accent,
    '--op-al': lift(accent, 0.52),
    '--op-at': alpha(accent, 0.22),
    '--op-glow': alpha(accent, 0.5),
  }) as CSSProperties;

// Each maker's page is its own design, not a theme of ours: colour, type, shape and light or dark all change.
// Unknown makers get the dark default.
export type Skin = 'dark' | 'light' | 'field' | 'sport' | 'site' | 'spa';
const SKINS: Record<string, Skin> = { lift: 'site', heatpump: 'light', mower: 'field', oven: 'dark', ebike: 'sport', battery: 'dark', robotmower: 'field', icemaker: 'sport', coffee: 'light', hottub: 'spa', bbq: 'dark', tv: 'sport', washer: 'light' };
export const skinOf = (s: Sector): Skin => SKINS[s.key] ?? 'dark';

// ---------- The device ----------
export function Device({ s, zoom, className, style, children, label }: { s: Sector; zoom?: number; className?: string; style?: CSSProperties; children: ReactNode; label?: string }) {
  usePhoneCss();
  return (
    <div className={['op-device', className].filter(Boolean).join(' ')} data-skin={skinOf(s)} style={{ ...vars(s.accent), ...(zoom ? { zoom } : null), ...style }} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <div className="op-screen">
        <i className="op-island" />
        <div className="op-sbar">
          <span>9:41</span>
          <span className="op-sbar-r">
            <svg width="15" height="9" viewBox="0 0 15 9" aria-hidden="true">
              <rect x="0" y="6" width="2.4" height="3" rx=".6" fill="currentColor" />
              <rect x="4" y="4" width="2.4" height="5" rx=".6" fill="currentColor" />
              <rect x="8" y="2" width="2.4" height="7" rx=".6" fill="currentColor" />
              <rect x="12" y="0" width="2.4" height="9" rx=".6" fill="currentColor" />
            </svg>
            <svg width="20" height="9" viewBox="0 0 20 9" aria-hidden="true">
              <rect x=".5" y=".5" width="16.5" height="8" rx="2.4" fill="none" stroke="currentColor" opacity=".45" />
              <rect x="2" y="2" width="12" height="5" rx="1.2" fill="currentColor" />
              <rect x="18" y="3" width="1.4" height="3" rx=".6" fill="currentColor" opacity=".45" />
            </svg>
          </span>
        </div>
        {children}
        <i className="op-homebar" />
      </div>
    </div>
  );
}

// ---------- Shared pieces ----------
function Row({ icon, t, sub, last }: { icon: IconName; t: string; sub: string; last?: boolean }) {
  return (
    <div className={last ? 'op-row is-last' : 'op-row'}>
      <span className="op-ico">
        <Icon name={icon} size={13} />
      </span>
      <span className="op-row-txt">
        <b>{t}</b>
        <span>{sub}</span>
      </span>
      <Icon name="chev" size={11} className="op-chev" />
    </div>
  );
}

function Done({ children, icon = 'check' }: { children: ReactNode; icon?: IconName }) {
  return (
    <div className="op-done">
      <span className="op-done-i">
        <Icon name={icon} size={11} stroke={2} />
      </span>
      {children}
    </div>
  );
}

function Checks({ items }: { items: string[] }) {
  return (
    <ul className="op-checks">
      {items.map((c) => (
        <li key={c}>
          <span className="op-tick">
            <Icon name="check" size={9} stroke={2.2} />
          </span>
          {c}
        </li>
      ))}
    </ul>
  );
}

// ---------- Screen: the unit's home, what opens on scan ----------
export function HomeScreen({ s }: { s: Sector }) {
  const c = COMPOSE[s.key];
  if (c) return <ComposedHome s={s} c={c} />;
  const d = phoneData(s);
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} />
        <div className="op-title">{s.fullModel}</div>
        <div className="op-status">
          <i />
          {d.status}
        </div>
      </div>
      <Hero s={s} callouts={d.callouts} pos={d.heroPos} />
      {d.live && (
        <div className="op-card op-live">
          <div className="op-live-txt">
            <span className="op-k">{d.live.label}</span>
            <span className="op-big">
              {d.live.value}
              <small>{d.live.unit}</small>
            </span>
          </div>
          <Spark series={d.live.series} />
          <span className="op-k2">{d.live.sub}</span>
        </div>
      )}
      <div className="op-card op-rows">
        <Row icon="parts" t="Parts that fit" sub={d.parts} />
        <Row icon="history" t="Service history" sub={d.service} />
        <Row icon="transfer" t="Pass it on" sub="The warranty goes with it" last />
      </div>
      <div className="op-ask">
        <Icon name="ask" size={14} />
        <span>Ask about your {s.model}</span>
        <span className="op-ask-go">
          <Icon name="send" size={11} stroke={2} />
        </span>
      </div>
    </div>
  );
}

// ---------- Screen: ask about this unit ----------
export function AskScreen({ s }: { s: Sector }) {
  return (
    <div className="op-page">
      <div className="op-nav">
        <Icon name="back" size={13} />
        <span>Ask your {s.model}</span>
        <span className="op-nav-sp" />
      </div>
      <div className="op-ctx">
        <span className="op-ctx-th">
          <img src={s.product} alt="" width={44} height={44} loading="lazy" />
        </span>
        <span className="op-ctx-t">
          <b>{s.fullModel}</b>
          <span className="op-num">{s.serial}</span>
        </span>
        <span className="op-pill">In warranty</span>
      </div>
      <div className="op-day">{s.ask.when}</div>
      <div className="op-q">{s.ask.q}</div>
      <div className="op-a">
        <span className="op-a-who">
          <span className="op-brand-mark is-sm">{s.mark}</span>
          {s.maker} assistant
        </span>
        <p>{s.ask.a}</p>
        <span className="op-src">
          <Icon name="doc" size={10} />
          {s.ask.src}
        </span>
      </div>
      <div className="op-sugs">
        <span>Book {s.partnerShort}</span>
        <span>Talk to a person</span>
      </div>
      <div className="op-input">
        <span>Ask a follow-up</span>
        <span className="op-ask-go">
          <Icon name="send" size={11} stroke={2} />
        </span>
      </div>
    </div>
  );
}

// ---------- Screen: installer or dealer handover ----------
const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

function Sig({ k }: { k: number }) {
  return (
    <svg width="64" height="18" viewBox="0 0 70 20" aria-hidden="true">
      <path
        d={k === 0 ? 'M2 14c6-10 10-10 9 0s6-9 11-4 7 2 10-2 8 6 14 1 9-4 12-2' : 'M2 12c4-6 9-8 9-2s-3 8 3 4 8-9 12-3 5 4 10 0 10-3 14 0 7 1 10-1'}
        fill="none"
        stroke="var(--op-ink, rgba(255,255,255,.85))"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function HandoverScreen({ s }: { s: Sector }) {
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} tag={`${cap(s.partnerRole)} view`} />
        <div className="op-title">{s.handoverName}</div>
        <div className="op-sub">
          {s.partner} · {s.dates.handover}
        </div>
      </div>
      <UnitLine s={s} />
      <div className="op-card op-pad">
        <Checks items={s.checks} />
      </div>
      <div className="op-sigs">
        {[`${s.partnerShort} technician`, s.owner1Short].map((n, k) => (
          <div key={n} className="op-card op-sig">
            <Sig k={k} />
            <span>{n}</span>
          </div>
        ))}
      </div>
      <Done icon="shield">Warranty active from today</Done>
    </div>
  );
}

function UnitLine({ s, note }: { s: Sector; note?: string }) {
  return (
    <div className="op-unit">
      <span className="op-ctx-th">
        <img src={s.product} alt="" width={44} height={44} loading="lazy" />
      </span>
      <span className="op-ctx-t">
        <b>{s.fullModel}</b>
        <span className="op-num">{note ?? s.serial}</span>
      </span>
    </div>
  );
}

// ---------- Screen: a warranty claim, evidence attached ----------
const CROPS = ['50% 35%', '20% 60%', '85% 55%'];

export function ClaimScreen({ s }: { s: Sector }) {
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} tag={`Claim ${s.claim.id}`} />
        <div className="op-title is-sm">{s.claim.fault}</div>
        <div className="op-status">
          <i />
          Approved · {s.dates.claim}
        </div>
      </div>
      <div className="op-photos">
        {CROPS.map((p, k) => (
          <span key={p}>
            <img src={k === 0 ? s.product : s.scene} alt="" width={140} height={140} loading="lazy" style={{ objectPosition: p }} />
            {k === 0 && <em>{s.claim.evidence[0]}</em>}
          </span>
        ))}
      </div>
      <div className="op-card op-pad">
        <Checks items={['Serial matched', 'Owner registered', 'In warranty', ...s.claim.evidence.slice(1).map((e) => `${e} attached`)]} />
      </div>
      <UnitLine s={s} note={`Filed by ${s.partnerShort}`} />
      <Done>Approved · {s.partnerShort} credited</Done>
    </div>
  );
}

// ---------- Screen: kept working, the routine service booked before anything breaks ----------
export function ServiceScreen({ s }: { s: Sector }) {
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} tag="Service due" />
        <div className="op-title is-sm">Time for its {s.service.name}</div>
        <div className="op-sub">Booked with {s.partner}</div>
      </div>
      <UnitLine s={s} />
      <div className="op-card op-rows">
        <Row icon="tool" t={cap(s.service.name)} sub={s.service.date} />
        <Row icon="parts" t="Parts ready" sub={cap(s.service.kit)} />
        <Row icon="doc" t={`Bulletin ${s.bulletin.code}`} sub="Done at the same visit" />
        <Row icon="history" t="Full history" sub="Shared with the engineer" last />
      </div>
      <div className="op-a">
        <span className="op-a-who">
          <span className="op-brand-mark is-sm">{s.mark}</span>
          Why now
        </span>
        <p>Servicing on time keeps it running at its best and keeps the warranty valid.</p>
      </div>
      <Done>Booked · {s.partnerShort} confirmed</Done>
    </div>
  );
}

// ---------- Screen: ownership transfer ----------
const initials = (n: string) =>
  n
    .replace(/,.*/, '')
    .split(/\s+/)
    .filter((w) => /^[A-Z]/.test(w) && w !== 'The' && w !== 'and')
    .slice(0, 2)
    .map((w) => w[0])
    .join('') || 'N';

export function TransferScreen({ s, records }: { s: Sector; records: number }) {
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} tag="Pass it on" />
        <div className="op-title">Change of owner</div>
        <div className="op-sub">{s.dates.sold}</div>
      </div>
      <div className="op-card op-xfer">
        {[
          ['From', s.owner1],
          ['To', s.owner2],
        ].map(([k, v], i) => (
          <div key={k} className="op-person">
            <span className={i ? 'op-av is-new' : 'op-av'}>{initials(v)}</span>
            <span className="op-ctx-t">
              <span className="op-k">{k}</span>
              <b>{v}</b>
            </span>
          </div>
        ))}
        <span className="op-xfer-line" />
      </div>
      <div className="op-card op-rows">
        <Row icon="history" t={`${records} history records`} sub="Come with the unit" />
        <Row icon="shield" t="Warranty carried over" sub={`To the end of its term`} />
        <Row icon="user" t="Previous owner kept private" sub="Consent signed" last />
      </div>
      <Done>Welcome, {s.owner2.split(',')[0]}</Done>
    </div>
  );
}

// ---------- Screen: product passport, recycler view ----------
export function PassportScreen({ s }: { s: Sector }) {
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} tag="Product passport" />
      </div>
      <div className="op-seg" role="presentation">
        <span>Owner</span>
        <span>Repairer</span>
        <span className="on">Recycler</span>
      </div>
      <div className="op-title is-sm" style={{ padding: '0 16px' }}>
        Return your {s.fullModel}
      </div>
      <div className="op-card op-rows">
        {s.materials.map((m, k) => (
          <Row key={m} icon={k === 0 ? 'leaf' : 'box'} t={m.split(':')[0]} sub={m.includes(':') ? cap(m.split(':')[1].trim()) : 'Logged against this serial'} last={k === s.materials.length - 1} />
        ))}
      </div>
      <div className="op-note">Example passport content. Your product group&rsquo;s rules decide the fields.</div>
      <div className="op-primary">
        <Icon name="recycle" size={13} />
        Book a take-back
      </div>
    </div>
  );
}

// ---------- Screen: the Digital Product Passport, the compliance side of the same code ----------
export function DppScreen({ s }: { s: Sector }) {
  const tabs = PASSPORT_TABS[s.key] ?? ['Public', 'Repairer', 'Recycler', 'Authority'];
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} tag="Product passport" />
        <div className="op-title is-sm">{s.fullModel}</div>
        <div className="op-sub">
          ID {s.serial} · GS1 Digital Link
        </div>
      </div>
      <div className="op-seg" role="presentation" style={{ gridTemplateColumns: `repeat(${tabs.length}, 1fr)` }}>
        {tabs.map((t, k) => (
          <span key={t} className={k === 0 ? 'on' : undefined}>
            {t}
          </span>
        ))}
      </div>
      <div className="op-card op-rows">
        {s.passport.map((m, k) => (
          <Row key={m} icon={passportIcon(m)} t={m.replace(/ \(.*\)$/, '')} sub={/\((.*)\)/.exec(m)?.[1] ?? 'From your ERP, on this serial'} last={k === s.passport.length - 1} />
        ))}
      </div>
      <div className="op-note">{PASSPORT_NOTE[s.key] ?? <>Fields follow your product group&rsquo;s rules under ESPR. Each role sees only what it may.</>}</div>
      <Done icon="shield">Passport live · same code as the owner page</Done>
    </div>
  );
}

// ---------- Two phones, overlapping at an angle: the unit's home in front, the question behind ----------
export function OwnerPair({ s, className, back = 'ask' }: { s: Sector; className?: string; back?: 'ask' | 'passport' }) {
  return (
    <div className={['op-pair', className].filter(Boolean).join(' ')}>
      <Device s={s} className="op-pair-back" label={back === 'passport' ? `The ${s.fullModel} Digital Product Passport on the same code, with a view for each role` : undefined}>
        {back === 'passport' ? <DppScreen s={s} /> : <AskScreen s={s} />}
      </Device>
      <Device s={s} className="op-pair-front" label={`The ${s.fullModel} page that opens when its owner scans the code: ${phoneData(s).status.toLowerCase()}, warranty, service, parts and answers, in ${s.maker}'s brand.`}>
        <HomeScreen s={s} />
      </Device>
    </div>
  );
}

// ---------- Lifecycle screens: unbox (register), set up, use, recycle ----------
export function RegisterScreen({ s }: { s: Sector }) {
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} tag="Welcome" />
        <div className="op-title">Your {s.model} is here</div>
        <div className="op-sub">Two taps and it is yours. No password.</div>
      </div>
      <UnitLine s={s} />
      <div className="op-card op-pad">
        <Checks items={[`Serial ${s.serial}`, `${cap(s.partnerRole)}: ${s.partner}`, 'Warranty starts today', 'Safety notices for this unit']} />
      </div>
      <div className="op-primary">
        <Icon name="shield" size={13} />
        Confirm it is mine
      </div>
    </div>
  );
}

export function SetupScreen({ s, steps }: { s: Sector; steps: string[] }) {
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} tag="Set up · 2 min" />
        <div className="op-title">Let&rsquo;s set up your {s.model}</div>
      </div>
      <div className="op-seg" role="presentation">
        <span className="on">English</span>
        <span>Deutsch</span>
        <span>Français</span>
      </div>
      <div className="op-card op-rows">
        {steps.map((t, k) => (
          <div key={t} className={k === steps.length - 1 ? 'op-row is-last' : 'op-row'}>
            <span className="op-ico" style={{ fontSize: 10, fontWeight: 700 }}>
              {k === 0 ? <Icon name="check" size={12} stroke={2.2} /> : k + 1}
            </span>
            <span className="op-row-txt">
              <b>{t}</b>
              <span>{k === 0 ? 'Done' : k === 1 ? 'Next · with a 40 second video' : 'Then'}</span>
            </span>
            <Icon name="chev" size={11} className="op-chev" />
          </div>
        ))}
      </div>
      <div className="op-ask">
        <Icon name="ask" size={14} />
        <span>Ask about setup</span>
        <span className="op-ask-go">
          <Icon name="send" size={11} stroke={2} />
        </span>
      </div>
    </div>
  );
}

export function UseScreen({ s, tip, q, a, src }: { s: Sector; tip: string; q: string; a: string; src: string }) {
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} tag="Tip of the week" />
        <div className="op-title is-sm">{tip}</div>
      </div>
      <div className="op-day">This evening</div>
      <div className="op-q">{q}</div>
      <div className="op-a">
        <span className="op-a-who">
          <span className="op-brand-mark is-sm">{s.mark}</span>
          {s.maker} assistant
        </span>
        <p>{a}</p>
        <span className="op-src">
          <Icon name="doc" size={10} />
          {src}
        </span>
      </div>
      <div className="op-sugs">
        <span>Book {s.partnerShort}</span>
        <span>Talk to a person</span>
      </div>
    </div>
  );
}

// ---------- In store: the display unit's code, in two modes ----------
/** The few fields the store screens need. Store products are not hero units, so they bring their own. */
export type StoreUnit = Pick<Sector, 'maker' | 'mark' | 'accent' | 'model' | 'fullModel' | 'product'> & {
  key: string;
  points: string[];
  compare: string;
  q: string;
  a: string;
  src: string;
};

export function StoreAdviserScreen({ u }: { u: StoreUnit }) {
  const s = u as unknown as Sector;
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} tag="Adviser mode" />
        <div className="op-title">Show the {u.model}</div>
        <div className="op-sub">Three things shoppers love about it</div>
      </div>
      <div className="op-card op-rows">
        {u.points.map((t, k) => (
          <div key={t} className={k === u.points.length - 1 ? 'op-row is-last' : 'op-row'}>
            <span className="op-ico" style={{ fontSize: 10, fontWeight: 700 }}>
              {k + 1}
            </span>
            <span className="op-row-txt">
              <b>{t}</b>
              <span>{k === 0 ? 'Show it · 30 sec clip' : 'Tap to show'}</span>
            </span>
            <Icon name="chev" size={11} className="op-chev" />
          </div>
        ))}
      </div>
      <div className="op-card op-rows">
        <Row icon="box" t="Compare side by side" sub={u.compare} />
        <Row icon="parts" t="Accessories that fit" sub="Add to the basket" last />
      </div>
      <div className="op-primary">
        <Icon name="send" size={12} />
        Send to the shopper&rsquo;s phone
      </div>
    </div>
  );
}

export function StoreSelfScreen({ u }: { u: StoreUnit }) {
  const s = u as unknown as Sector;
  return (
    <div className="op-page">
      <div className="op-head">
        <Brand s={s} tag="In store" />
        <div className="op-title is-sm">Ask about the {u.model}</div>
      </div>
      <div className="op-q">{u.q}</div>
      <div className="op-a">
        <span className="op-a-who">
          <span className="op-brand-mark is-sm">{u.mark}</span>
          {u.maker} assistant
        </span>
        <p>{u.a}</p>
        <span className="op-src">
          <Icon name="doc" size={10} />
          {u.src}
        </span>
      </div>
      <div className="op-sugs">
        <span>Compare models</span>
        <span>Call an adviser</span>
      </div>
      <div className="op-ask">
        <Icon name="ask" size={14} />
        <span>Ask another question</span>
        <span className="op-ask-go">
          <Icon name="send" size={11} stroke={2} />
        </span>
      </div>
    </div>
  );
}
